import React, { useState, useEffect } from 'react';
import { Truck } from 'lucide-react';
import Page from '../../../components/Page';
import PeriodFilter from '../../../components/PeriodFilter';
import EnhancedVehicleFreightDashboard, { KpiModalProvider } from './EnhancedVehicleFreightDashboard';
import { useAnalyzeVehicleFreightByPeriodMutation, MonthRange } from '../../../services/api';
import { useOwnerHeaders } from '../../../contexts/OwnerContext';
import { motion } from 'framer-motion';
import { AIAssistantButton } from '../../../components/AIAssistant';

// Cache configuration
const CACHE_KEY = 'vehicle_freight_analysis_cache';
const CACHE_EXPIRY = 1000 * 60 * 60; // 1 hour

interface CachedData {
  data: any;
  timestamp: number;
  company: string;
  year: number;
  month_ranges: number[];
}

const getCacheKey = (company: string, year: number, month_ranges: number[]) =>
  `${CACHE_KEY}-${company}-${year}-${month_ranges.join('-')}`;

const getCachedData = (company: string, year: number, month_ranges: number[]): CachedData | null => {
  const key = getCacheKey(company, year, month_ranges);
  const cached = localStorage.getItem(key);
  if (cached) {
    const parsed = JSON.parse(cached);
    if (Date.now() - parsed.timestamp < CACHE_EXPIRY) {
      return parsed;
    } else {
      localStorage.removeItem(key); // Cache expired
    }
  }
  return null;
};

const setCachedData = (data: any, company: string, year: number, month_ranges: number[]) => {
  const key = getCacheKey(company, year, month_ranges);
  const cacheData: CachedData = {
    data,
    timestamp: Date.now(),
    company,
    year,
    month_ranges,
  };
  localStorage.setItem(key, JSON.stringify(cacheData));
};

const VehicleFreightAnalysisPage: React.FC = () => {
  const ownerHeaders = useOwnerHeaders();
  const [analyzeVehicleFreight, { isLoading: isSearching, error: searchError, data: apiAnalysisData }] = useAnalyzeVehicleFreightByPeriodMutation();
  const [hasSearched, setHasSearched] = useState(false);
  const [cachedAnalysisData, setCachedAnalysisData] = useState<any>(null);

  // Load cached data on component mount
  useEffect(() => {
    if (ownerHeaders?.company) {
      // Try to get any cached data for the company
      const allKeys = Object.keys(localStorage).filter(key => key.startsWith(`${CACHE_KEY}-${ownerHeaders.company}`));
      if (allKeys.length > 0) {
        const latestKey = allKeys[allKeys.length - 1];
        const cached = localStorage.getItem(latestKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Date.now() - parsed.timestamp < CACHE_EXPIRY) {
            // Mostrar dados em cache automaticamente
            setHasSearched(true);
            setCachedAnalysisData(parsed.data);
          }
        }
      }
    }
  }, [ownerHeaders]);

  // Use cached data or API data
  const aiAnalysisData = apiAnalysisData || cachedAnalysisData;

  const handlePeriodFilter = async (year: number, monthRanges: MonthRange[]) => {
    if (!ownerHeaders?.company) {
      // Error occurred;
      return;
    }

    // Convert MonthRange[] to number[] (extract startMonth from each range)
    const month_ranges = monthRanges.map(range => range.startMonth);

    const cached = getCachedData(ownerHeaders.company, year, month_ranges);
    if (cached) {
      // If cached, mostrar dados cacheados sem fazer nova requisição
      setCachedAnalysisData(cached.data);
      setHasSearched(true);
      return;
    }

    try {
      setCachedAnalysisData(null); // Limpar dados em cache antigos
      setHasSearched(false); // Reset before new search
      const result = await analyzeVehicleFreight({ year, month_ranges }).unwrap();
      if (result.success) {
        setCachedData(result, ownerHeaders.company, year, month_ranges);
        setHasSearched(true);
      } else {
        // Error occurred;
        setHasSearched(true); // Still show the display, but it will be empty/error
      }
    } catch (err) {
      // Error occurred;
      setHasSearched(true); // Show error state
    }
  };

  return (
    <Page title="🚛 Análise de Fretes por Veículo com IA">
      <div className="space-y-6">
        {/* Period Filter */}
        <PeriodFilter
          onFilter={handlePeriodFilter}
          isLoading={isSearching}
        />

        {/* Loading State */}
        {isSearching && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-20"
          >
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mb-4"></div>
            <p className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>
              🤖 IA analisando fretes por veículo...
            </p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Processando dados reais e gerando insights estratégicos por tipo de veículo
            </p>
          </motion.div>
        )}

        {/* Error State */}
        {searchError && hasSearched === false && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 rounded-xl"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '2px solid #ef4444'
            }}
          >
            <div className="flex items-center space-x-3 mb-2">
              <span className="text-2xl">❌</span>
              <h3 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                Erro ao buscar dados
              </h3>
            </div>
            <p style={{ color: 'var(--text-secondary)' }}>
              {'error' in searchError && typeof searchError.error === 'string'
                ? searchError.error
                : 'Erro interno do servidor'}
            </p>
          </motion.div>
        )}

        {/* Empty State - Only show if searched but no data */}
        {!isSearching && hasSearched && !aiAnalysisData && !searchError && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-20"
          >
            <Truck className="w-24 h-24 mb-4" style={{ color: 'var(--text-secondary)', opacity: 0.5 }} />
            <h3 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
              Nenhum dado encontrado
            </h3>
            <p className="text-center max-w-md" style={{ color: 'var(--text-secondary)' }}>
              Não foram encontrados dados de fretes por veículo para o período selecionado.
              Tente selecionar outro período.
            </p>
          </motion.div>
        )}

        {/* Placeholder - Show before any search */}
        {!isSearching && !hasSearched && !aiAnalysisData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-20"
          >
            <Truck className="w-24 h-24 mb-4" style={{ color: 'var(--text-secondary)', opacity: 0.5 }} />
            <h3 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
              Selecione um período para análise
            </h3>
            <p className="text-center max-w-md" style={{ color: 'var(--text-secondary)' }}>
              Escolha o ano e os meses desejados no filtro acima para visualizar
              a análise inteligente de fretes por veículo com IA.
            </p>
          </motion.div>
        )}

        {/* AI Analysis Display */}
        {!isSearching && hasSearched && aiAnalysisData && (
          <>
            {/* AI Assistant Button */}
            {(() => {
              // Contexto normalizado para Fretes por Veículo
              const screenContext = 'Vehicles'

              return <AIAssistantButton screenContext={screenContext} />
            })()}

            <KpiModalProvider>
              <EnhancedVehicleFreightDashboard
                data={aiAnalysisData}
                isLoading={isSearching}
                error={searchError}
              />
            </KpiModalProvider>
          </>
        )}
      </div>
    </Page>
  );
};

export default VehicleFreightAnalysisPage;
